/**
 * User Management Page
 * Task 17.5: User管理UIの構築
 * Requirements: 20.1, 20.2, 20.3, 20.4, 20.5, 20.6, 20.7, 20.8, 20.9
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Checkbox,
  CircularProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon, VpnKey as VpnKeyIcon } from '@mui/icons-material';
import {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  bulkUpdateUsers,
  changeUserPassword,
  User,
  GetUsersParams,
  CreateUserInput,
  UpdateUserInput,
} from '../api/userManagementApi';
import { PasswordChangeDialog, PasswordChangeDialogUser } from '../components/PasswordChangeDialog';
import { organizationApi } from '../api/organizationApi';
import type { Company, Department } from '../types/organization.types';

export default function UserManagementPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Filters
  const [filters, setFilters] = useState<GetUsersParams>({});
  const [usernameFilter, setUsernameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [permissionLevelFilter, setPermissionLevelFilter] = useState('');

  // Dialogs
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form state
  const [formData, setFormData] = useState<UpdateUserInput>({});

  // Selection state for bulk operations
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  
  // Bulk update dialog state
  const [bulkUpdateDialogOpen, setBulkUpdateDialogOpen] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [bulkUpdateCompanyId, setBulkUpdateCompanyId] = useState('');
  const [bulkUpdateDepartmentId, setBulkUpdateDepartmentId] = useState('');
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);

  // Password change dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordDialogUser, setPasswordDialogUser] = useState<PasswordChangeDialogUser | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Load users
  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetUsersParams = {
        ...filters,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      const response = await getUsers(params);
      setUsers(response.users);
      setTotalCount(response.pagination.total);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page, rowsPerPage, filters]);

  // Load companies for bulk update
  const loadCompanies = async () => {
    try {
      const data = await organizationApi.getCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Failed to load companies', err);
    }
  };

  // Load departments when company is selected
  const loadDepartments = async (companyId: string) => {
    if (!companyId) {
      setDepartments([]);
      return;
    }
    try {
      const data = await organizationApi.getDepartmentsByCompany(companyId);
      setDepartments(data);
    } catch (err) {
      console.error('Failed to load departments', err);
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    setFilters({
      username: usernameFilter || undefined,
      email: emailFilter || undefined,
      permissionLevel: permissionLevelFilter || undefined,
    });
    setPage(0);
  };

  // Clear filters
  const handleClearFilters = () => {
    setUsernameFilter('');
    setEmailFilter('');
    setPermissionLevelFilter('');
    setFilters({});
    setPage(0);
  };

  // Pagination handlers
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // View user
  const handleView = async (id: string) => {
    try {
      const user = await getUserById(id);
      setSelectedUser(user);
      setViewDialogOpen(true);
    } catch (err) {
      setError('Failed to load user details');
      console.error(err);
    }
  };

  // Edit user
  const handleEdit = async (id: string) => {
    try {
      const user = await getUserById(id);
      setSelectedUser(user);
      setFormData({
        name: user.name,
        jobTitle: user.jobTitle,
        permissionLevel: user.permissionLevel,
        role: user.role,
      });
      setEditDialogOpen(true);
    } catch (err) {
      setError('Failed to load user details');
      console.error(err);
    }
  };

  // Save user
  const handleSave = async () => {
    if (!selectedUser) return;
    
    try {
      await updateUser(selectedUser.id, formData);
      setEditDialogOpen(false);
      setSelectedUser(null);
      setFormData({});
      loadUsers();
    } catch (err) {
      setError('Failed to update user');
      console.error(err);
    }
  };

  // Delete user
  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;
    
    try {
      await deleteUser(selectedUser.id);
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadUsers();
    } catch (err) {
      setError('Failed to delete user');
      console.error(err);
    }
  };

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedUserIds(new Set(users.map(u => u.id)));
    } else {
      setSelectedUserIds(new Set());
    }
  };

  const handleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUserIds);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUserIds(newSelected);
  };

  // Bulk update handlers
  const handleOpenBulkUpdate = async () => {
    await loadCompanies();
    setBulkUpdateCompanyId('');
    setBulkUpdateDepartmentId('');
    setDepartments([]);
    setBulkUpdateDialogOpen(true);
  };

  const handleBulkUpdateCompanyChange = async (companyId: string) => {
    setBulkUpdateCompanyId(companyId);
    setBulkUpdateDepartmentId('');
    await loadDepartments(companyId);
  };

  const handleBulkUpdateConfirm = async () => {
    if (selectedUserIds.size === 0) return;
    if (!bulkUpdateCompanyId && !bulkUpdateDepartmentId) {
      setError('会社または所属を選択してください');
      return;
    }

    setBulkUpdateLoading(true);
    try {
      const result = await bulkUpdateUsers({
        userIds: Array.from(selectedUserIds),
        companyId: bulkUpdateCompanyId || undefined,
        departmentId: bulkUpdateDepartmentId || undefined,
      });
      
      setBulkUpdateDialogOpen(false);
      setSelectedUserIds(new Set());
      loadUsers();
      
      if (result.failedCount > 0) {
        setError(`${result.successCount}件成功、${result.failedCount}件失敗`);
      }
    } catch (err) {
      setError('一括更新に失敗しました');
      console.error(err);
    } finally {
      setBulkUpdateLoading(false);
    }
  };

  // Password change handlers
  const handlePasswordChange = (user: User) => {
    setPasswordDialogUser({
      id: user.id,
      username: user.username,
      email: user.email,
      name: user.name,
    });
    setPasswordDialogOpen(true);
  };

  const handlePasswordChangeSubmit = async (
    userId: string,
    newPassword: string,
    forcePasswordReset: boolean
  ) => {
    await changeUserPassword(userId, { newPassword, forcePasswordReset });
    setSuccessMessage('パスワードを変更しました');
  };



  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        User Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Username"
            value={usernameFilter}
            onChange={(e) => setUsernameFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Email"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <TextField
            select
            label="Permission Level"
            value={permissionLevelFilter}
            onChange={(e) => setPermissionLevelFilter(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="SELF_ONLY">Self Only</MenuItem>
            <MenuItem value="DEPARTMENT">Department</MenuItem>
            <MenuItem value="COMPANY_WIDE">Company Wide</MenuItem>
          </TextField>
          <Button variant="contained" onClick={handleApplyFilters}>
            Apply
          </Button>
          <Button variant="outlined" onClick={handleClearFilters}>
            Clear
          </Button>
        </Box>
      </Paper>

      {/* Bulk Actions */}
      {selectedUserIds.size > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ color: 'primary.contrastText' }}>
              {selectedUserIds.size}件選択中
            </Typography>
            <Button 
              variant="contained" 
              color="secondary"
              onClick={handleOpenBulkUpdate}
            >
              会社・所属を一括変更
            </Button>
            <Button 
              variant="outlined" 
              sx={{ color: 'white', borderColor: 'white' }}
              onClick={() => setSelectedUserIds(new Set())}
            >
              選択解除
            </Button>
          </Box>
        </Paper>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedUserIds.size > 0 && selectedUserIds.size < users.length}
                  checked={users.length > 0 && selectedUserIds.size === users.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Permission</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow 
                  key={user.id}
                  selected={selectedUserIds.has(user.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedUserIds.has(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                    />
                  </TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>{user.permissionLevel}</TableCell>
                  <TableCell>{user.company?.name || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="詳細">
                      <IconButton size="small" onClick={() => handleView(user.id)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="編集">
                      <IconButton size="small" onClick={() => handleEdit(user.id)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="パスワード変更">
                      <IconButton size="small" onClick={() => handlePasswordChange(user)}>
                        <VpnKeyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="削除">
                      <IconButton size="small" onClick={() => handleDeleteClick(user)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </TableContainer>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>User Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="ID" value={selectedUser.id} InputProps={{ readOnly: true }} />
              <TextField label="Username" value={selectedUser.username} InputProps={{ readOnly: true }} />
              <TextField label="Email" value={selectedUser.email} InputProps={{ readOnly: true }} />
              <TextField label="Name" value={selectedUser.name} InputProps={{ readOnly: true }} />
              <TextField label="Job Title" value={selectedUser.jobTitle || '-'} InputProps={{ readOnly: true }} />
              <TextField label="Role" value={selectedUser.role} InputProps={{ readOnly: true }} />
              <TextField label="Permission Level" value={selectedUser.permissionLevel} InputProps={{ readOnly: true }} />
              <TextField label="Company" value={selectedUser.company?.name || '-'} InputProps={{ readOnly: true }} />
              <TextField label="Created At" value={new Date(selectedUser.createdAt).toLocaleString()} InputProps={{ readOnly: true }} />
              <TextField label="Updated At" value={new Date(selectedUser.updatedAt).toLocaleString()} InputProps={{ readOnly: true }} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Job Title"
              value={formData.jobTitle || ''}
              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
            />
            <TextField
              select
              label="Role"
              value={formData.role || ''}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as 'USER' | 'ADMIN' })}
            >
              <MenuItem value="USER">User</MenuItem>
              <MenuItem value="ADMIN">Admin</MenuItem>
            </TextField>
            <TextField
              select
              label="Permission Level"
              value={formData.permissionLevel || ''}
              onChange={(e) => setFormData({ ...formData, permissionLevel: e.target.value as any })}
            >
              <MenuItem value="SELF_ONLY">Self Only</MenuItem>
              <MenuItem value="DEPARTMENT">Department</MenuItem>
              <MenuItem value="COMPANY_WIDE">Company Wide</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user "{selectedUser?.name}" ({selectedUser?.email})?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Update Dialog */}
      <Dialog 
        open={bulkUpdateDialogOpen} 
        onClose={() => setBulkUpdateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>会社・所属の一括変更</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {selectedUserIds.size}件のユーザーの会社・所属を変更します。
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="会社"
              value={bulkUpdateCompanyId}
              onChange={(e) => handleBulkUpdateCompanyChange(e.target.value)}
              fullWidth
            >
              <MenuItem value="">選択してください</MenuItem>
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="所属（部署）"
              value={bulkUpdateDepartmentId}
              onChange={(e) => setBulkUpdateDepartmentId(e.target.value)}
              fullWidth
              disabled={!bulkUpdateCompanyId}
            >
              <MenuItem value="">選択してください</MenuItem>
              {departments.map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUpdateDialogOpen(false)}>キャンセル</Button>
          <Button 
            onClick={handleBulkUpdateConfirm} 
            variant="contained"
            disabled={bulkUpdateLoading || (!bulkUpdateCompanyId && !bulkUpdateDepartmentId)}
          >
            {bulkUpdateLoading ? <CircularProgress size={24} /> : '変更を適用'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <PasswordChangeDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        user={passwordDialogUser}
        onSubmit={handlePasswordChangeSubmit}
      />

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
